import { useState, useCallback } from 'react';
import type { MouseEvent } from 'react';
import { useDispatch } from 'react-redux';
import { setUiWorkspaceContextMenuAction } from '@/domain/ui/actions';

interface AnchorByElement {
  anchorEl: EventTarget & Element;
}

interface AnchorByPosition {
  anchorReference: 'anchorPosition';
  anchorPosition: {
    left: number;
    top: number;
  };
}

type MenuAnchor = AnchorByElement | AnchorByPosition;

interface MenuState {
  status: boolean;
  anchor: MenuAnchor | null;
  id: string | null;
}

interface ContextMenuReturn {
  menu: MenuState | null;
  contextMenuHide: () => void;
  handleContextMenu: (event: MouseEvent<Element>, id: string | null) => void;
}

export function useContextMenu(): ContextMenuReturn {
  const dispatch = useDispatch();
  const [menu, setMenu] = useState<MenuState | null>(null);

  const handleContextMenu = useCallback(
    (event: MouseEvent<Element>, id: string | null) => {
      event.preventDefault();

      let anchor: MenuAnchor | null = null;
      if (event.type === 'click') {
        anchor = { anchorEl: event.currentTarget };
      } else if (event.type === 'contextmenu') {
        anchor = {
          anchorReference: 'anchorPosition',
          anchorPosition: {
            left: event.clientX + 2,
            top: event.clientY - 6,
          },
        };
      }

      setMenu({
        status: true,
        anchor,
        id,
      });

      dispatch(setUiWorkspaceContextMenuAction(true));
    },
    [dispatch],
  );

  const contextMenuHide = useCallback(() => {
    setMenu((prev) =>
      prev
        ? {
            ...prev,
            anchor: null,
            status: false,
          }
        : null,
    );

    dispatch(setUiWorkspaceContextMenuAction(false));
  }, [dispatch]);

  return {
    menu,
    contextMenuHide,
    handleContextMenu,
  };
}
